import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import TradingChart from "../components/tradezone/TradingChart";
import { Target, ArrowRight, Zap, Wallet, ChevronDown, BarChart2, List, ArrowUpDown, History, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import MarketList from "../components/tradezone/MarketList";
import OrderBookPanel from "../components/tradezone/OrderBookPanel";
import ActiveTrades from "../components/ActiveTrades";
import { message } from 'antd';
import { useTradingContext } from "../context/TradingContext";

export default function FuturesTrading() {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const { selectedPair, currentMarketData, getWalletBalance, setTradingMode, createOrder } = useTradingContext();
  const [leverage, setLeverage] = useState(20);
  const [activeTab, setActiveTab] = useState("long");
  const [marginMode, setMarginMode] = useState("Isolated");
  
  const [orderType, setOrderType] = useState("limit");
  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTpSl, setShowTpSl] = useState(false);
  const [amountCurrency, setAmountCurrency] = useState('crypto');
  const [takeProfit, setTakeProfit] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [stopPrice, setStopPrice] = useState("");

  // Mobile Tab State
  const [mobileView, setMobileView] = useState("trade"); 
  
  // Market Selection Modal
  const [isMarketOpen, setIsMarketOpen] = useState(false);

  useEffect(() => {
    setTradingMode('futures');
  }, [setTradingMode]);

  useEffect(() => {
    if (orderType === 'market' || price === "") {
        setPrice(currentMarketData.price);
    }
  }, [currentMarketData.price, orderType, price]);

  useEffect(() => {
    setIsMarketOpen(false);
  }, [selectedPair]);

  const quoteAsset = selectedPair?.quote_symbol || selectedPair?.quote_asset || "USDT";
  const baseAsset = selectedPair?.base_symbol || selectedPair?.base_asset || "BTC";
  const availableBalance = getWalletBalance(quoteAsset);

  const handlePercentageClick = (pct) => {
      const parsedPrice = parseFloat(price) || currentMarketData.price;
      if (!parsedPrice || parsedPrice <= 0) return;
      // L'utilisateur tape la marge réelle (ex: 100 USDT), le levier multiplie pour donner la position
      let marginAmount = availableBalance * pct;
      if (pct === 1.0) {
          marginAmount = marginAmount * 0.98; // Leave 2% for fees/spread to avoid insufficient balance errors
      }

      if (amountCurrency === 'usdt') {
          // En mode USDT, on remplit avec la marge (l'utilisateur tape 100 → 100)
          setAmount(marginAmount.toFixed(2));
      } else {
          // En mode crypto, on convertit la marge en quantité de crypto
          setAmount((marginAmount / parsedPrice).toFixed(6));
      }
  };

  const handleCreateOrder = async (side) => {
      let timeoutId;
      try {
          if (!amount || parseFloat(amount) <= 0) {
              message.error(t('trading.invalid_amount'));
              return;
          }

          if (orderType !== 'market' && (!price || parseFloat(price) <= 0)) {
              message.error(t('trading.invalid_price'));
              return;
          }

          let finalQuantity = parseFloat(amount);
          const execPrice = parseFloat(price) || currentMarketData.price;
          if (execPrice > 0) {
              if (amountCurrency === 'usdt') {
                  // Mode USDT : le montant tapé est la marge en USDT (ex: 100 USDT)
                  // Position = marge × levier, puis converti en quantité crypto
                  finalQuantity = (finalQuantity * parseInt(leverage)) / execPrice;
              } else {
                  // Mode crypto : le montant tapé est la marge en crypto (ex: 0.5 BTC)
                  // Position = marge × levier
                  finalQuantity = finalQuantity * parseInt(leverage);
              }
          }

          setIsSubmitting(true);
          
          // Sécurité : Reset isSubmitting après 35s si pas de réponse du service/api
          timeoutId = setTimeout(() => {
              setIsSubmitting(false);
          }, 35000);

          const pairId = selectedPair.id ? Number(selectedPair.id) : null;
          if (!pairId || isNaN(pairId)) {
              message.error(t('trading.invalid_pair'));
              setIsSubmitting(false);
              clearTimeout(timeoutId);
              return;
          }

          const orderData = {
              pair_id: pairId,
              order_type: orderType.toLowerCase(),
              side: side === 'long' ? 'buy' : 'sell', 
              leverage: parseInt(leverage),
              margin_mode: marginMode.toLowerCase(),
              quantity: finalQuantity,
              take_profit: takeProfit ? parseFloat(takeProfit) : null,
              stop_loss: stopLoss ? parseFloat(stopLoss) : null
          };
          if (orderType !== 'market') orderData.price = parseFloat(price);
          if (orderType === 'stop') orderData.trigger_price = parseFloat(stopPrice);
          
          await createOrder(orderData);
          setAmount("");
          messageApi.success(t('trading.order_created'));
      } catch (error) {
          console.error(error);
          messageApi.error(t('trading.error') + ': ' + (error.response?.data?.message || error.message));
      } finally {
          setIsSubmitting(false);
          if (timeoutId) clearTimeout(timeoutId);
      }
  };

  const parsedAmount = parseFloat(amount) || 0;
  const parsedPrice = parseFloat(price) || currentMarketData.price;
  // En mode USDT : le montant tapé est la marge, la position = marge × leverage
  // En mode crypto : le montant tapé est la marge en crypto, la position = (marge × leverage) × prix
  const estimatedCost = amountCurrency === 'usdt'
    ? (parsedAmount * leverage).toFixed(2)
    : ((parsedAmount * leverage) * parsedPrice).toFixed(2);

  const OrderForm = () => (
    <div className="trading-card h-full p-2 flex flex-col overflow-y-auto no-scrollbar">

        {/* Margin & Leverage (Binance Style Top Bar) */}
        <div className="flex items-center gap-2 mb-3">
            <button 
                onClick={() => setMarginMode(marginMode === "Isolated" ? "Cross" : "Isolated")}
                className="flex-1 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-[10px] font-black text-slate-300 uppercase"
            >
                {t(`trading.${marginMode.toLowerCase()}`)}
            </button>
            <div className="flex-1 relative group">
                <div className="flex items-center w-full bg-slate-800 rounded px-1 border border-transparent focus-within:border-white/20">
                    <input 
                        type="number" min="1" max="125" 
                        value={leverage} 
                        onChange={(e) => setLeverage(e.target.value)} 
                        className="w-full py-1.5 bg-transparent text-center text-[10px] font-black text-slate-300 uppercase focus:outline-none" 
                    />
                    <span className="text-[10px] font-black text-slate-500 pr-1">x</span>
                </div>
                <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-white/10 rounded-lg p-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl">
                    <input type="range" min="1" max="125" value={leverage} onChange={(e) => setLeverage(e.target.value)} className="w-full accent-rose-500" />
                </div>
            </div>
        </div>

        {/* Order Type Select */}
        <div className="flex p-0.5 bg-slate-950 rounded border border-white/5 mb-3">
            {['limit', 'market', 'stop'].map(type => (
                <button 
                    key={type}
                    onClick={() => setOrderType(type)}
                    className={`flex-1 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all ${orderType === type ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                >
                    {t(`trading.${type}`)}
                </button>
            ))}
        </div>

        <div className="space-y-3">
            {orderType === 'stop' && (
                <div className="space-y-0.5">
                    <input 
                        type="number" 
                        className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                        value={stopPrice}
                        onChange={(e) => setStopPrice(e.target.value)}
                        placeholder="Stop Price"
                    />
                </div>
            )}

            <div className="space-y-0.5">
                 <input 
                    type="number" 
                    className={`w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-xs text-white ${orderType === 'market' ? 'opacity-40 cursor-not-allowed' : ''}`}
                    value={orderType === 'market' ? currentMarketData.price : price}
                    onChange={(e) => setPrice(e.target.value)}
                    readOnly={orderType === 'market'}
                    placeholder="Price"
                 />
            </div>

            <div className="relative">
                 <input 
                    type="number" 
                    className="w-full bg-slate-900 border border-white/10 rounded pl-2 pr-14 py-1.5 text-xs text-white"
                    placeholder="Amount" 
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                />
                <button 
                    onClick={() => setAmountCurrency(c => c === 'crypto' ? 'usdt' : 'crypto')}
                    className="absolute right-1 top-1 bottom-1 px-1 bg-slate-800 rounded text-[9px] font-black text-slate-300 flex items-center gap-0.5"
                >
                    {amountCurrency === 'crypto' ? baseAsset : quoteAsset} <ChevronDown size={10}/>
                </button>
            </div>

            {/* Slider / Percentages */}
            <div className="flex justify-between items-center px-1">
                <div className="w-full h-1 bg-slate-800 rounded relative">
                    {[0.25, 0.50, 0.75, 1.0].map((pct, idx) => (
                        <button key={pct} onClick={() => handlePercentageClick(pct)} className="absolute w-2.5 h-2.5 bg-slate-400 rounded-full top-1/2 -translate-y-1/2 hover:bg-white hover:scale-125 transition-all" style={{ left: `${pct * 100}%`, transform: `translate(-50%, -50%)` }}></button>
                    ))}
                    <div className="absolute w-2.5 h-2.5 bg-slate-400 rounded-full top-1/2 left-0 -translate-y-1/2 hover:bg-white hover:scale-125 transition-all" onClick={() => handlePercentageClick(0)}></div>
                </div>
            </div>

            {/* Avail Balance */}
            <div className="flex items-center justify-between px-1 pt-1">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1">
                    Avbl
                </span>
                <span className="text-[10px] font-black text-white font-outfit">
                    {availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteAsset}
                </span>
            </div>

            {/* TP / SL Checkbox */}
            <div className="flex items-center gap-2 px-1 mt-1">
                <input 
                    type="checkbox" 
                    id="tpsl"
                    checked={showTpSl}
                    onChange={(e) => setShowTpSl(e.target.checked)}
                    className="w-3 h-3 accent-rose-500 bg-slate-900 border-white/10 rounded cursor-pointer"
                />
                <label htmlFor="tpsl" className="text-[10px] font-bold text-slate-400 cursor-pointer">TP/SL</label>
            </div>

            {showTpSl && (
                <div className="flex gap-2">
                    <input 
                        type="number" 
                        placeholder="Take Profit" 
                        value={takeProfit}
                        onChange={e => setTakeProfit(e.target.value)}
                        className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-[10px] text-white"
                    />
                    <input 
                        type="number" 
                        placeholder="Stop Loss" 
                        value={stopLoss}
                        onChange={e => setStopLoss(e.target.value)}
                        className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-[10px] text-white"
                    />
                </div>
            )}

            <div className="pt-2 space-y-1">
                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                    <span>Max Open</span>
                    <span className="text-white">{(availableBalance * leverage).toFixed(2)} {quoteAsset}</span>
                </div>
                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                    <span>Cost</span>
                    <span className="text-white">{estimatedCost} {quoteAsset}</span>
                </div>
                
                <div className="flex flex-col gap-1.5 mt-2">
                    <button 
                        onClick={() => handleCreateOrder('long')}
                        disabled={isSubmitting}
                        className={`w-full py-2.5 rounded font-black text-[12px] uppercase flex justify-between items-center px-4 transition-all ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'} bg-emerald-500 hover:bg-emerald-400 text-white`}
                    >
                        <span>Open Long</span>
                        <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">{t('trading.buy')}</span>
                    </button>
                    <button 
                        onClick={() => handleCreateOrder('short')}
                        disabled={isSubmitting}
                        className={`w-full py-2.5 rounded font-black text-[12px] uppercase flex justify-between items-center px-4 transition-all ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'} bg-rose-500 hover:bg-rose-400 text-white`}
                    >
                        <span>Open Short</span>
                        <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">{t('trading.sell')}</span>
                    </button>
                </div>
            </div>
       </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-inter flex flex-col md:h-screen md:overflow-hidden">
      {contextHolder}
      <Header />
      
      <main className="flex-1 flex flex-col gap-1.5 p-1.5 md:mt-[70px] md:overflow-hidden relative pb-24 md:pb-0">

        
        {/* Mobile Top Bar – Binance Style */}
        <div className="md:hidden flex items-center justify-between px-2 py-3 shrink-0">
          <button
            onClick={() => setIsMarketOpen(true)}
            className="flex items-center gap-2"
          >
            <span className="text-base font-black font-outfit text-white tracking-tight">
              {selectedPair?.symbol || "BTCUSDT"} <span className="text-[9px] text-rose-500 font-black">PERP</span>
            </span>
            <ChevronDown size={14} className="text-slate-500" />
            <span className={`text-xs font-black ml-1 ${
              currentMarketData.changePercent >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}>
              {currentMarketData.changePercent >= 0 ? '+' : ''}{currentMarketData.changePercent.toFixed(2)}%
            </span>
          </button>
          <div className={`text-xl font-black font-outfit ${
            currentMarketData.changePercent >= 0 ? 'text-emerald-400' : 'text-rose-400'
          }`}>
            {currentMarketData.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>

        {/* Futures Control Bar – Desktop Only */}
        <div className="hidden md:flex bg-slate-900/40 border border-white/5 rounded-xl px-4 py-2 flex-wrap items-center gap-x-8 gap-y-2 shrink-0 backdrop-blur-md relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.5)]"></div>
            
            <div 
                onClick={() => setIsMarketOpen(true)}
                className="flex items-center gap-3 pr-6 border-r border-white/5 cursor-pointer hover:bg-white/5 transition-all rounded-lg p-1 -ml-1 group"
            >
                <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center text-rose-500 shrink-0 group-hover:bg-rose-500/20">
                    <Target size={20} />
                </div>
                <div>
                    <div className="flex items-center gap-1.5">
                        <h2 className="text-lg font-black font-outfit uppercase tracking-tight text-white leading-none">
                            {selectedPair?.symbol || "BTCUSDT"} <span className="text-[9px] text-rose-500 align-top ml-1 font-black">PERP</span>
                        </h2>
                        <ChevronDown size={14} className="text-slate-500 group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mt-1">{t('trading.futures_trading')}</p>
                </div>
            </div>

            <div className="flex items-center gap-6 md:gap-10">
                <div className="space-y-0.5">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest leading-none">{t('trading.current_price')}</p>
                    <div className="text-lg font-black font-outfit text-white leading-none">
                        {currentMarketData.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                </div>
                
                <div className="space-y-0.5">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest leading-none">{t('trading.variation_24h')}</p>
                    <div className={`text-xs font-black font-outfit ${currentMarketData.changePercent >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {currentMarketData.changePercent >= 0 ? '+' : ''}{currentMarketData.changePercent.toFixed(2)}%
                    </div>
                </div>
            </div>

            <div className="ml-auto hidden md:flex items-center gap-3">
                <button 
                  onClick={() => setMarginMode(marginMode === "Isolated" ? "Cross" : "Isolated")}
                  className="px-3 py-1.5 bg-slate-950 border border-white/10 rounded-lg hover:bg-slate-800 transition-all group"
                >
                    <span className="text-[9px] font-black text-slate-400 group-hover:text-indigo-400 uppercase tracking-widest">{t(`trading.${marginMode.toLowerCase()}`)}</span>
                </button>
                <div className="px-3 py-1.5 bg-rose-500/10 border border-rose-500/30 rounded-lg shadow-[0_0_15px_rgba(244,63,94,0.1)]">
                    <span className="text-[9px] font-black text-rose-500 uppercase tracking-widest">{leverage}x</span>
                </div>
            </div>
        </div>

        {/* Mobile View Toggles – Binance-style pill tabs */}
        <div className="lg:hidden flex gap-2 px-1 mb-1 shrink-0 overflow-x-auto no-scrollbar">
            {[
                { id: 'chart', icon: BarChart2, label: 'Graphe' },
                { id: 'trade', icon: ArrowUpDown, label: 'Futures' }
            ].map(tab => (
                <button 
                    key={tab.id}
                    onClick={() => setMobileView(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all duration-200 border ${
                      mobileView === tab.id
                        ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 shadow-lg shadow-rose-500/10'
                        : 'border-white/5 bg-slate-900/50 text-slate-500 hover:text-slate-300'
                    }`}
                >
                    <tab.icon size={12} />
                    {tab.label}
                </button>
            ))}
        </div>


        {/* Main Trading Area */}
        <div className="flex-1 flex flex-col lg:flex-row gap-1.5 min-h-0 px-1 pb-1 overflow-hidden">
            
            {/* Left: Market List (Desktop Only) */}
            <div className="hidden lg:block w-[260px] shrink-0">
                <MarketList />
            </div>

            {/* Center: Chart + Active Trades */}
            <div className={`flex-1 flex flex-col gap-1.5 min-w-0 ${mobileView === 'chart' || mobileView === 'orders' ? 'flex h-full' : 'hidden lg:flex'}`}>
                {/* Chart Area */}
                <div className={`flex-1 rounded-2xl overflow-hidden border border-white/5 relative shadow-inner ${mobileView === 'chart' ? 'block h-full' : 'hidden lg:block'}`}>
                    <TradingChart />
                </div>
                
                {/* Active Trades (Bottom) */}
                <div className={`shrink-0 overflow-hidden border border-white/5 rounded-2xl bg-slate-900/20 ${mobileView === 'orders' ? 'block h-full' : 'hidden lg:block h-[35%]'}`}>
                    <ActiveTrades />
                </div>
            </div>

            {/* Right: Order Book + Order Form + Active Trades (Mobile) */}
            <div className={`w-full lg:max-w-[320px] shrink-0 flex flex-col gap-1.5 h-full ${mobileView === 'trade' ? 'flex overflow-y-auto pb-6' : 'hidden lg:flex'}`}>
                <div className="flex flex-row lg:flex-col gap-1.5 shrink-0 h-[460px] lg:h-full">
                    {/* Order Form */}
                    <div className={`flex-[1.4] lg:flex-none lg:shrink-0 h-full lg:h-[520px]`}>
                        {OrderForm()}
                    </div>
                    {/* Order Book */}
                    <div className={`flex-1 min-w-0 h-full overflow-hidden`}>
                        <OrderBookPanel />
                    </div>
                </div>
                
                {/* Positions Underneath on Mobile */}
                <div className="flex-1 min-h-[300px] lg:hidden overflow-hidden border border-white/5 rounded-2xl bg-slate-900/20 mt-1 shrink-0">
                    <ActiveTrades />
                </div>
            </div>
        </div>


        {/* Mobile Market Modal – Binance-style bottom sheet */}
        {isMarketOpen && (
            <div className="fixed inset-0 z-[200] flex flex-col" onClick={() => setIsMarketOpen(false)}>
                <div className="flex-1 bg-black/60 backdrop-blur-sm" />
                <div
                  className="bg-slate-900 rounded-t-3xl border-t border-white/10 flex flex-col shadow-2xl animate-slide-up"
                  style={{ maxHeight: '85vh' }}
                  onClick={e => e.stopPropagation()}
                >
                    {/* Drag Handle */}
                    <div className="flex justify-center pt-3 pb-1">
                      <div className="w-10 h-1 rounded-full bg-white/10" />
                    </div>
                    <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-1 h-6 rounded-full bg-rose-500" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-white">Contrats Futures</h3>
                        </div>
                        <button 
                            onClick={() => setIsMarketOpen(false)}
                            className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden p-2">
                        <MarketList />
                    </div>
                </div>
            </div>
        )}
      </main>
    </div>
  );
}
